import React, { useEffect, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon, InboxIcon, XIcon } from 'lucide-react';
import type { OrgQueue } from '../../types/offboarding';
import { FieldLabel } from './Section';
interface QueueComboboxProps {
  id: string;
  label: string;
  placeholder: string;
  options: OrgQueue[];
  value: string;
  onChange: (id: string) => void;
  required?: boolean;
}
export function QueueCombobox({
  id,
  label,
  placeholder,
  options,
  value,
  onChange,
  required
}: QueueComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);
  const selected = options.find(o => o.id === value);
  const results = options.filter(o => o.name.toLowerCase().includes(query.trim().toLowerCase()));
  const listboxId = `${id}-listbox`;
  const choose = (queue: OrgQueue) => {
    onChange(queue.id);
    setOpen(false);
    setQuery('');
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex(i => Math.min(i + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && open && results[activeIndex]) {
      e.preventDefault();
      choose(results[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };
  return <div ref={containerRef} data-id="element-282" data-mp="2sfz537dy3ur8">
      <FieldLabel htmlFor={id} required={required} data-id="element-283" data-mp="2sf5vlynlqr5e">
        {label}
      </FieldLabel>
      <div className="relative" data-id="element-284" data-mp="2kerhnh8jrodx">
        <InboxIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" data-id="element-285" data-mp="2kdyib1bx415t" />
        <input ref={inputRef} id={id} type="text" role="combobox" autoComplete="off" aria-expanded={open} aria-controls={listboxId} aria-activedescendant={open && results[activeIndex] ? `${id}-option-${results[activeIndex].id}` : undefined} value={open ? query : selected?.name ?? ''} onChange={e => {
        setQuery(e.target.value);
        setActiveIndex(0);
        setOpen(true);
      }} onFocus={() => {
        setOpen(true);
        setQuery('');
        setActiveIndex(0);
      }} onKeyDown={handleKeyDown} placeholder={open && selected ? selected.name : placeholder} className={`block w-full rounded-lg border bg-white py-2.5 pl-9 pr-9 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-navy-600 focus:outline-none focus:ring-1 focus:ring-navy-600 ${selected ? 'border-gray-300 font-medium' : 'border-gray-300'}`} data-id="element-286" data-mp="2u4t3s3hcawq7" />
        {selected && !open ? <button type="button" onClick={() => onChange('')} aria-label={`Clear ${label}`} className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700" data-id="element-287" data-mp="3kejwjaosgeny">
            <XIcon className="h-3.5 w-3.5" data-id="element-288" data-mp="366jc0xq78op" />
          </button> : <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" data-id="element-289" data-mp="77rtvfmsfavq" />}

        {open && <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg" data-id="element-290" data-mp="zpw23sqzqrpn">
            {results.length === 0 ? <p className="px-3 py-2.5 text-xs text-gray-500" data-id="element-291" data-mp="3373qxvuc163w">No queues match “{query.trim()}”</p> : <ul id={listboxId} role="listbox" className="max-h-56 overflow-y-auto py-1" data-id="element-292" data-mp="2y77pa393xqri">
                {results.map((queue, index) => <li key={queue.id} id={`${id}-option-${queue.id}`} role="option" aria-selected={queue.id === value} onMouseDown={e => {
            e.preventDefault();
            choose(queue);
          }} onMouseEnter={() => setActiveIndex(index)} className={`flex cursor-pointer items-center justify-between px-3 py-2 text-sm text-gray-900 ${index === activeIndex ? 'bg-navy-50' : ''}`} data-id="element-293" data-mp="2u7ytytyf5ktu" data-mpi={index}>
                    {queue.name}
                    {queue.id === value && <CheckIcon className="h-4 w-4 text-navy-600" data-id="element-294" data-mp="1reuxiib4y2it" />}
                  </li>)}
              </ul>}
          </div>}
      </div>
    </div>;
}